import type { DriveStep } from "driver.js";
import { ONBOARDING_IDS } from "../constants";

export const tournamentOverviewSteps: DriveStep[] = [
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_SELECTOR}`,
        popover: {
            title: "Tournament Selector",
            description:
                "Switch between tournaments from the dropdown — every section on the page updates instantly to reflect the selected tournament.",
            side: "bottom",
            align: "end",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_EXPORT}`,
        popover: {
            title: "Export to Excel",
            description:
                "Download the full tournament analysis as an Excel file — includes all metrics, match results, and player stats for the selected tournament.",
            side: "bottom",
            align: "end",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_NAV}`,
        popover: {
            title: "Tournament Sections",
            description:
                "Navigate between the three tournament views: Overview (this page), Compare (stack players side by side), and Team Ranking (detailed team-level statistics).",
            side: "bottom",
            align: "start",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_METRICS}`,
        popover: {
            title: "Key Metrics",
            description:
                "At-a-glance KPI cards for the selected tournament — win rate, average KPI, goals scored, and more. Each card shows the delta compared to the previous tournament so you can spot trends immediately.",
            side: "bottom",
            align: "center",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_HOME_AWAY}`,
        popover: {
            title: "Standings & Home / Away Split",
            description:
                "Use the tabs at the top to toggle between the full tournament standings table and the home/away performance breakdown — win rate, goals per game, goals against, and record for each venue type.",
            side: "right",
            align: "start",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_GAMES_SUMMARY}`,
        popover: {
            title: "Game Result Summary",
            description:
                "A quick breakdown of all match outcomes: regular wins, overtime wins, overtime losses, and regular losses — with the overall win percentage shown at the bottom.",
            side: "left",
            align: "end",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_TOP_PERFORMERS}`,
        popover: {
            title: "Top Performers",
            description:
                "The highest-KPI players in this tournament, ranked with gold/silver/bronze icons. Click any player's name to jump directly to their full profile.",
            side: "right",
            align: "start",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_RECENT_GAMES}`,
        popover: {
            title: "Recent Match Results",
            description:
                "A log of recent matches with the result badge (Win / Win OT / Loss), opponent, date, and final score. The upcoming match is pinned at the top when scheduled.",
            side: "left",
            align: "center",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_QUICK_STATS}`,
        popover: {
            title: "Quick Stats",
            description:
                "Six key percentage metrics — shot accuracy, face-off win %, save %, and more. Click the info icon next to any stat to see a per-player breakdown chart for that metric.",
            side: "left",
            align: "end",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_CHART}`,
        popover: {
            title: "Match-by-Match Trend",
            description:
                "A visual line/bar chart tracking one metric across every match in the tournament. Each data point is labeled with the value and an up/down trend arrow compared to the previous game.",
            side: "top",
            align: "center",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_CHART_TABS}`,
        popover: {
            title: "Chart Metric Tabs",
            description:
                "Switch what the chart above is plotting: Goals Scored, Goals Against, Points Earned, Goal Difference, High Risk Shots, High Risk Shots Against, KPI vs Goal Difference, Shots Saved %, or Line Performance.",
            side: "top",
            align: "center",
        },
    },
    {
        element: `#${ONBOARDING_IDS.TOURNAMENT_ACHIEVEMENTS}`,
        onHighlightStarted: (el) => el?.scrollIntoView({ behavior: "instant", block: "center" }),
        popover: {
            title: "Tournament Achievements",
            description:
                "Badges earned by the team and individual players during this tournament, highlighting standout collective and individual moments across the competition.",
            side: "top",
            align: "center",
        },
    },
];
